import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  CircularProgress,
  Avatar,
  InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import GroupIcon from '@mui/icons-material/Group';
import ApartmentIcon from '@mui/icons-material/Apartment';
import SearchIcon from '@mui/icons-material/Search';
import { contractorsApi } from '../api/contractors';
import { showSoftSuccess } from '../utils/softSuccess';

const ContractorsPage = () => {
  const [contractors, setContractors] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedContractorId, setSelectedContractorId] = useState('');
  const [accessList, setAccessList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    email: '',
    full_name: '',
    password: '',
  });

  const [accessForm, setAccessForm] = useState({
    tenant_id: '',
    access_level: 'read',
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [contractorsData, tenantsData] = await Promise.all([
          contractorsApi.listContractors(),
          contractorsApi.listTenants(),
        ]);
        setContractors(contractorsData);
        setTenants(tenantsData);
      } catch (err) {
        setError(err?.response?.data?.detail || err?.message || 'Ошибка загрузки данных');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const selectedContractor = useMemo(
    () => contractors.find((c) => String(c.id) === String(selectedContractorId)) || null,
    [contractors, selectedContractorId]
  );
  const filteredContractors = useMemo(() => {
    if (!searchTerm) return contractors;
    const q = searchTerm.toLowerCase();
    return contractors.filter((c) => {
      const name = String(c.full_name || '').toLowerCase();
      const email = String(c.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [contractors, searchTerm]);

  useEffect(() => {
    if (!selectedContractorId) {
      setAccessList([]);
      return;
    }
    const loadAccess = async () => {
      setAccessLoading(true);
      setError('');
      try {
        const data = await contractorsApi.listAccess(selectedContractorId);
        setAccessList(data);
      } catch (err) {
        setError(err?.response?.data?.detail || err?.message || 'Ошибка загрузки доступов');
      } finally {
        setAccessLoading(false);
      }
    };
    loadAccess();
  }, [selectedContractorId]);

  const handleChange = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (!form.email || !form.password) {
      setError('Укажите email и пароль');
      return;
    }
    try {
      const created = await contractorsApi.createContractor(form);
      setContractors((prev) => [created, ...prev]);
      setForm({ email: '', full_name: '', password: '' });
      setSuccess('Подрядчик добавлен');
      showSoftSuccess('Подрядчик добавлен');
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Ошибка создания подрядчика');
    }
  };

  const handleAccessChange = (key) => (event) => {
    setAccessForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleGrantAccess = async () => {
    if (!selectedContractorId) {
      setError('Сначала выберите подрядчика');
      return;
    }
    if (!accessForm.tenant_id) {
      setError('Выберите клиента');
      return;
    }
    setError('');
    setSuccess('');
    try {
      const payload = {
        contractor_user_id: selectedContractorId,
        tenant_id: accessForm.tenant_id,
        access_level: accessForm.access_level,
      };
      await contractorsApi.grantAccess(payload);
      const data = await contractorsApi.listAccess(selectedContractorId);
      setAccessList(data);
      setSuccess('Доступ обновлён');
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Ошибка обновления доступа');
    }
  };

  const handleRevoke = async (tenantId) => {
    if (!selectedContractorId) return;
    setError('');
    setSuccess('');
    try {
      await contractorsApi.revokeAccess(selectedContractorId, tenantId);
      setAccessList((prev) => prev.filter((item) => String(item.tenant_id) !== String(tenantId)));
      setSuccess('Доступ отозван');
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Ошибка при отзыве доступа');
    }
  };

  const contractorsCount = contractors.length;
  const tenantsCount = tenants.length;
  const accessCount = accessList.length;

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: '100vh',
        width: '100%',
        background: 'radial-gradient(1200px 800px at 10% 0%, rgba(225,232,255,0.6) 0%, rgba(245,246,255,0.2) 55%, rgba(255,255,255,0) 100%)',
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Подрядчики</Typography>
          <Typography color="text.secondary">
            Управление аккаунтами подрядчиков и доступом к клиентам
          </Typography>
        </Box>
        {selectedContractor && (
          <Chip
            label={`Выбран: ${selectedContractor.full_name || selectedContractor.email}`}
            color="primary"
            variant="outlined"
            sx={{ mt: { xs: 1, md: 0 } }}
          />
        )}
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.25, borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 12px 30px rgba(32, 40, 70, 0.08)' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark' }}>
                <GroupIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Подрядчики</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{contractorsCount}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.25, borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 12px 30px rgba(32, 40, 70, 0.08)' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ bgcolor: 'secondary.light', color: 'secondary.dark' }}>
                <ApartmentIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Клиенты</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{tenantsCount}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.25, borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 12px 30px rgba(32, 40, 70, 0.08)' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ bgcolor: 'success.light', color: 'success.dark' }}>
                <PersonAddAltIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Доступы</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{accessCount}</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            md: '320px 1fr',
            lg: '360px 1fr',
            xl: '400px 1fr',
          },
          alignItems: 'stretch',
        }}
      >
        <Stack spacing={2} sx={{ height: '100%' }}>
            <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 16px 40px rgba(32, 40, 70, 0.1)' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <PersonAddAltIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Создать подрядчика</Typography>
                <Typography variant="body2" color="text.secondary">Новый аккаунт в системе</Typography>
              </Box>
            </Stack>
            <Box component="form" onSubmit={handleCreate} noValidate autoComplete="off">
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  value={form.email}
                  onChange={handleChange('email')}
                  fullWidth
                />
                <TextField
                  label="Полное имя"
                  value={form.full_name}
                  onChange={handleChange('full_name')}
                  fullWidth
                />
                <TextField
                  label="Пароль"
                  type="password"
                  value={form.password}
                  onChange={handleChange('password')}
                  fullWidth
                />
                <Button type="submit" variant="contained" size="large">
                  Создать
                </Button>
              </Stack>
            </Box>
            </Paper>

            <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 16px 40px rgba(32, 40, 70, 0.08)', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 320 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Список подрядчиков</Typography>
              <Chip size="small" label={contractorsCount} />
            </Stack>
            <TextField
              placeholder="Поиск по имени или email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 1.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {loading ? (
              <Stack alignItems="center" sx={{ py: 3 }}>
                <CircularProgress size={24} />
              </Stack>
            ) : (
              <List dense sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
                {filteredContractors.map((contractor) => (
                  <ListItemButton
                    key={contractor.id}
                    selected={String(contractor.id) === String(selectedContractorId)}
                    onClick={() => setSelectedContractorId(String(contractor.id))}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      border: '1px solid transparent',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(81, 70, 255, 0.08)',
                        borderColor: 'rgba(81, 70, 255, 0.3)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={contractor.full_name || contractor.email}
                      secondary={contractor.email}
                    />
                  </ListItemButton>
                ))}
                {!filteredContractors.length && (
                  <Typography color="text.secondary">Нет подрядчиков</Typography>
                )}
              </List>
            )}
            </Paper>

            
          </Stack>

        <Paper sx={{ p: 2.75, minHeight: 520, borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 18px 50px rgba(32, 40, 70, 0.1)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Доступы подрядчика
              </Typography>
              {selectedContractor && <Chip size="small" label={`${accessCount} доступов`} />}
            </Stack>
            {!selectedContractor && (
              <Typography color="text.secondary">Выберите подрядчика слева</Typography>
            )}
            {selectedContractor && (
              <>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2, bgcolor: 'rgba(81, 70, 255, 0.04)' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark' }}>
                      {(selectedContractor.full_name || selectedContractor.email || '?')[0]?.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">
                        {selectedContractor.full_name || selectedContractor.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedContractor.email}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ mb: 2, alignItems: { xs: 'stretch', sm: 'flex-end' } }}
                >
                  <FormControl fullWidth>
                    <InputLabel id="tenant-select-label">Клиент</InputLabel>
                    <Select
                      labelId="tenant-select-label"
                      label="Клиент"
                      value={accessForm.tenant_id}
                      onChange={handleAccessChange('tenant_id')}
                    >
                      {tenants.map((tenant) => (
                        <MenuItem key={tenant.id} value={tenant.id}>
                          {tenant.name} ({tenant.schema_name})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel id="access-level-label">Уровень</InputLabel>
                    <Select
                      labelId="access-level-label"
                      label="Уровень"
                      value={accessForm.access_level}
                      onChange={handleAccessChange('access_level')}
                    >
                      <MenuItem value="read">Только чтение</MenuItem>
                      <MenuItem value="write">Редактирование</MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleGrantAccess}
                    sx={{
                      minWidth: 170,
                      height: 56,
                      alignSelf: { xs: 'stretch', sm: 'center' },
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 12px 24px rgba(81, 70, 255, 0.25)',
                    }}
                  >
                    Назначить доступ
                  </Button>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                {accessLoading ? (
                  <Stack alignItems="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </Stack>
                ) : (
                  <Stack spacing={1}>
                    {accessList.map((access) => (
                      <Paper
                        key={`${access.contractor_user_id}-${access.tenant_id}`}
                        variant="outlined"
                        sx={{ p: 1.5, borderRadius: 2 }}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography variant="subtitle2">
                              {access.tenant_name || access.tenant_schema || access.tenant_id}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {access.tenant_schema || 'schema не указана'}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              size="small"
                              label={access.access_level === 'write' ? 'Редактирование' : 'Только чтение'}
                              color={access.access_level === 'write' ? 'success' : 'default'}
                              variant="outlined"
                            />
                            <IconButton onClick={() => handleRevoke(access.tenant_id)} size="small">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                    {!accessList.length && (
                      <Typography color="text.secondary">Нет доступов</Typography>
                    )}
                  </Stack>
                )}
              </>
            )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ContractorsPage;
